"use client";

import type { SelectItemProps as RadixSelectItemProps } from "@radix-ui/react-select";
import { Item, ItemIndicator, ItemText } from "@radix-ui/react-select";
import type { Ref } from "react";
import { useContext } from "react";
import { FaCheck } from "react-icons/fa6";

import { cn } from "../../../utils";
import { SelectContext } from "../Select";

export interface SelectItemProps<T extends string> extends Omit<
  RadixSelectItemProps,
  "value"
> {
  ref?: Ref<HTMLDivElement>;
  className?: string;
  itemComponent?: React.ReactNode;
  value: T;
  children?: React.ReactNode;
  showCheck?: boolean;
}

export const SelectItem = <T extends string>({
  ref,
  children,
  className,
  itemComponent,
  showCheck,
  ...props
}: SelectItemProps<T>) => {
  const variant = useContext(SelectContext);
  const isMiniVariant = variant === "mini";
  const isBuiltinVariant = variant === "builtin";
  const isToolbarVariant = variant === "toolbar";

  return (
    <Item
      className={cn(
        "ui:group ui:relative ui:flex ui:h-12 ui:cursor-pointer ui:items-center ui:justify-between ui:border-b ui:border-divider ui:bg-buttonBg ui:px-3 ui:transition-colors ui:select-none ui:last:border-b-0 ui:hover:bg-buttonHover ui:focus:outline-none ui:dark:border-divider ui:dark:bg-buttonFill ui:dark:hover:bg-buttonHover",
        {
          "ui:h-4.5 ui:px-1 ui:text-xxs ui:data-[state=checked]:bg-buttonBg ui:dark:data-[state=checked]:bg-buttonFill":
            isMiniVariant,
          "ui:h-5 ui:bg-transparent ui:px-1 ui:text-xs ui:data-[state=checked]:bg-transparent ui:dark:bg-transparent ui:dark:data-[state=checked]:bg-transparent":
            isBuiltinVariant,
          "ui:bg-background": isToolbarVariant,
        },
        className,
      )}
      {...props}
      ref={ref}
    >
      {itemComponent ?? (
        <>
          <ItemText>{children}</ItemText>
          {showCheck ? (
            <ItemIndicator asChild>
              <FaCheck
                className={cn(
                  "ui:absolute ui:right-3.5 ui:text-brandBlue ui:transition-opacity ui:group-hover:opacity-0",
                  {
                    "ui:right-1": isBuiltinVariant,
                  },
                )}
              />
            </ItemIndicator>
          ) : null}
        </>
      )}
      {isMiniVariant ? (
        <ItemIndicator>
          <FaCheck className="ui:text-brandBlue" />
        </ItemIndicator>
      ) : null}
    </Item>
  );
};
